/**
 * CRE "General Report / No Template" helpers (Hebrew template content).
 * Resolve the backend template from GetTemplatesMulty by name / prompt metadata.
 * Do not hardcode templateId or template HTML — those come from backend.
 * UI: CRE-only and Hebrew report language for now (`language`, not dictationLanguage).
 *
 * PromptTypeName order:
 * 1) template.promptFile (or promptTypeName) from API
 * 2) anatomy catalog promptFile for the template's anatomy
 * 3) CRE_UNIVERSAL_NO_TEMPLATE_PROMPT_v1.txt (fallback only, never overrides 1–2)
 */

/** Last-resort PromptTypeName when backend template/anatomy omit promptFile. */
export const NO_TEMPLATE_PROMPT_FALLBACK =
  "CRE_UNIVERSAL_NO_TEMPLATE_PROMPT_v1.txt";

/** Backend template name confirmed by Jacky (Hebrew General Report). */
export const GENERAL_REPORT_HEBREW_TEMPLATE_NAME = "General Report Hebrew";

const NO_TEMPLATE_NAMES = new Set([
  "general report hebrew",
  "no template",
  "ללא תבנית",
]);

const NO_TEMPLATE_PROMPT_MARKER = "CRE_UNIVERSAL_NO_TEMPLATE_PROMPT_v1";

/**
 * Report/template language is `language` from useI18nLanguage in Patient
 * (same value used for GetTemplatesMulty + Process AI Language) — not dictationLanguage.
 * @param {string | null | undefined} language
 * @returns {boolean}
 */
export function isHebrewReportLanguage(language) {
  return String(language || "")
    .toLowerCase()
    .split(/[-_]/)[0] === "he";
}

/**
 * @param {Record<string, unknown> | null | undefined} template
 * @returns {boolean}
 */
export function isNoTemplateTemplate(template) {
  if (!template || typeof template !== "object") return false;
  const name = String(
    template.templateName ?? template.TemplateName ?? "",
  )
    .trim()
    .toLowerCase();
  if (name && NO_TEMPLATE_NAMES.has(name)) return true;

  const prompt = String(
    template.promptFile ??
      template.PromptFile ??
      template.promptTypeName ??
      template.PromptTypeName ??
      "",
  );
  return prompt.includes(NO_TEMPLATE_PROMPT_MARKER);
}

/**
 * @param {unknown} templates
 * @returns {Record<string, unknown> | null}
 */
export function findNoTemplateTemplate(templates) {
  if (!Array.isArray(templates)) return null;
  const byPreferredName = templates.find((t) => {
    const name = String(t?.templateName ?? t?.TemplateName ?? "")
      .trim()
      .toLowerCase();
    return name === GENERAL_REPORT_HEBREW_TEMPLATE_NAME.toLowerCase();
  });
  if (byPreferredName) return byPreferredName;
  return templates.find((t) => isNoTemplateTemplate(t)) || null;
}

/**
 * Hide the system General Report / No Template row from the CRE picker / voice list.
 * @param {unknown} templates
 * @returns {unknown[]}
 */
export function excludeNoTemplateFromPicker(templates) {
  if (!Array.isArray(templates)) return [];
  return templates.filter((t) => !isNoTemplateTemplate(t));
}

/**
 * Resolve PromptTypeName for No Template / General Report mode.
 * Backend template promptFile always wins; fallback is never an override.
 *
 * @param {Record<string, unknown> | null | undefined} template
 * @param {Array<{ code?: string, promptFile?: string }> | null | undefined} anathomies
 * @returns {string}
 */
export function resolveNoTemplatePromptTypeName(template, anathomies) {
  const direct = String(
    template?.promptFile ??
      template?.PromptFile ??
      template?.promptTypeName ??
      template?.PromptTypeName ??
      "",
  ).trim();
  if (direct) return direct;

  const anatomyCode = String(
    template?.anathomy ?? template?.Anathomy ?? "",
  ).trim();
  if (anatomyCode && Array.isArray(anathomies)) {
    const found = anathomies.find((a) => a && String(a.code) === anatomyCode);
    const fromAnatomy = String(found?.promptFile ?? "").trim();
    if (fromAnatomy) return fromAnatomy;
  }

  return NO_TEMPLATE_PROMPT_FALLBACK;
}
